import type { FC, PointerEvent, ReactNode } from 'react'
import type { GlobeControl } from '@/components/three/globe-scene'

import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { WebGLBoundary, supportsWebGL } from '@/components/three/webgl-boundary'
import { GLOBE_DESCRIPTION, GLOBE_PLACES } from '@/components/three/globe-data'

interface ExportGlobeProps {
    className?: string
    showLabels?: boolean
}

/* Still globe shown on the server, while the 3D scene loads, and where WebGL is not available */
export const GlobeFallback: FC = (): ReactNode => {
    return (
        <div
            aria-hidden='true'
            className='absolute inset-[9.5%] rounded-full bg-[radial-gradient(circle_at_34%_30%,#235c41_0%,#0f2a1e_46%,#07150e_78%)] shadow-[0_0_120px_-10px_rgba(201,162,74,0.45),inset_0_0_70px_rgba(201,162,74,0.2)]'
        >
            <div className='absolute inset-0 rounded-full bg-[radial-gradient(rgba(201,162,74,0.55)_1px,transparent_1.6px)] bg-[length:11px_11px] opacity-60 [mask-image:radial-gradient(circle_at_42%_40%,black_25%,transparent_70%)]' />
        </div>
    )
}

const GlobeScene = dynamic(() => import('@/components/three/globe-scene'), {
    ssr: false,
    loading: () => <GlobeFallback />
})

export const ExportGlobe: FC<ExportGlobeProps> = ({ className, showLabels = true }): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const labelRefs = useRef<Array<HTMLElement | null>>([])
    const control = useRef<GlobeControl>({ dragging: false, lastX: 0, lastY: 0, offsetX: 0, offsetY: 0, velocityY: 0 })
    const inView = useInView(containerRef)
    const reducedMotion = useReducedMotion()
    const [webgl, setWebgl] = useState<boolean | null>(null)

    useEffect(() => {
        setWebgl(supportsWebGL())
    }, [])

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const drag = control.current

        drag.dragging = true
        drag.lastX = event.clientX
        drag.lastY = event.clientY
        drag.velocityY = 0
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = control.current

        if (!drag.dragging) return

        const deltaX = event.clientX - drag.lastX
        const deltaY = event.clientY - drag.lastY

        drag.lastX = event.clientX
        drag.lastY = event.clientY
        drag.offsetY += deltaX * 0.0065
        drag.velocityY = deltaX * 0.0065
        drag.offsetX = Math.max(-0.45, Math.min(0.45, drag.offsetX + deltaY * 0.004))
    }

    const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
        control.current.dragging = false

        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    }

    const interactive = webgl === true

    return (
        <div
            ref={containerRef}
            role='img'
            aria-label={GLOBE_DESCRIPTION}
            onPointerDown={interactive ? onPointerDown : undefined}
            onPointerMove={interactive ? onPointerMove : undefined}
            onPointerUp={interactive ? onPointerEnd : undefined}
            onPointerCancel={interactive ? onPointerEnd : undefined}
            style={{ touchAction: 'pan-y' }}
            className={cn('relative aspect-square w-full select-none', interactive && 'cursor-grab active:cursor-grabbing', className)}
        >
            {interactive ? (
                <WebGLBoundary fallback={<GlobeFallback />}>
                    <GlobeScene control={control} labelRefs={labelRefs} active={inView} animate={!reducedMotion} />
                </WebGLBoundary>
            ) : (
                <GlobeFallback />
            )}

            {interactive && showLabels && (
                <div aria-hidden='true' dir='ltr' className='pointer-events-none absolute inset-0 overflow-hidden'>
                    {GLOBE_PLACES.map((place, index) => (
                        <div
                            key={place.id}
                            ref={element => {
                                labelRefs.current[index] = element
                            }}
                            className='absolute left-0 top-0 opacity-0 transition-opacity duration-500 will-change-transform'
                        >
                            <span
                                dir='rtl'
                                className={cn(
                                    'block whitespace-nowrap rounded-full border px-2.5 py-1 text-[0.68rem] font-semibold leading-5 shadow-lg backdrop-blur-sm sm:text-xs',
                                    index === 0
                                        ? '-translate-x-1/2 -translate-y-[calc(100%_+_16px)] border-gold bg-gold text-forest-950'
                                        : '-translate-x-1/2 translate-y-3 border-gold/40 bg-forest-950/80 text-cream'
                                )}
                            >
                                {place.name}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
